import { ArrowRight } from "lucide-react";
import { useState } from "react";

import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime, parseDateInput } from "@/shared/utils/dateTimeHelpers";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { formatTaxLabel } from "@/shared/utils/paymentTaxes";

import { countryLabel } from "../../constants/paymentTaxes.constants";
import type { PaymentTax } from "../../types/paymentTaxes.types";
import { shortId } from "../../utils/feeFormat";
import type { TaxChange, TaxChangeStatus } from "../../utils/taxCoverage";
import { CountryFlag } from "../fees/CorridorIdentity";
import { scopeLabel } from "./taxScopeLabels";

export interface TaxChangeTimelineProps {
  changes: TaxChange[];
  organizationNames: Record<string, string>;
  authorNames: Record<string, string>;
  /** The feed says where each change applies; a corridor's own history does not need to. */
  showScope?: boolean;
  groupByDay?: boolean;
}

const STATUS_PILL: Record<Exclude<TaxChangeStatus, "ACTIVE">, "info" | "neutral"> = {
  SCHEDULED: "info",
  SUPERSEDED: "neutral",
};

function dayKey(iso: string): string {
  const date = parseDateInput(iso);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** Same reading as the price book's timeline: who, what it was, what it is, since when and why. */
export function TaxChangeTimeline({
  changes,
  organizationNames,
  authorNames,
  showScope = false,
  groupByDay = false,
}: TaxChangeTimelineProps) {
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  const [now] = useState(() => Date.now());
  const today = dayKey(new Date(now).toISOString());
  const yesterday = dayKey(new Date(now - 86_400_000).toISOString());
  const dayLabel = (iso: string) => {
    const key = dayKey(iso);
    if (key === today) return t("feed_today");
    if (key === yesterday) return t("feed_yesterday");
    return formatDateTime(iso, { locale: activeLanguage, includeTime: false });
  };
  const timeOf = (iso: string) =>
    parseDateInput(iso).toLocaleTimeString(activeLanguage, { hour: "2-digit", minute: "2-digit" });
  const rateText = (tax: PaymentTax) =>
    compareDecimalStrings(tax.rate, "0") === 0
      ? `${tax.name} · ${t("exempt")}`
      : formatTaxLabel(tax, activeLanguage);
  const role = (tax: PaymentTax) =>
    tax.organization_id
      ? t("history_role_own", {
          name: organizationNames[tax.organization_id] ?? shortId(tax.organization_id),
        })
      : t("source_general");
  const author = (tax: PaymentTax) =>
    tax.created_by_user_id
      ? (authorNames[tax.created_by_user_id] ?? shortId(tax.created_by_user_id))
      : t("history_unknown_author");
  const period = (change: TaxChange) => {
    const from = formatDateTime(change.tax.valid_from, { locale: activeLanguage });
    if (change.status === "SCHEDULED") return t("period_starts", { from });
    if (!change.effectiveUntil) return t("period_open", { from });
    return t("period_range", { from, to: formatDateTime(change.effectiveUntil, { locale: activeLanguage }) });
  };

  return (
    <ol className="divide-y divide-border">
      {changes.map((change, index) => {
        const { tax } = change;
        const showDay =
          groupByDay && (index === 0 || dayKey(changes[index - 1].tax.created_at) !== dayKey(tax.created_at));
        return (
          <li key={tax.id} className={cn(showDay && index > 0 && "border-t-0")}>
            {showDay && (
              <p
                className={cn(
                  "pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
                  index > 0 ? "pt-6" : "pt-1",
                )}
              >
                {dayLabel(tax.created_at)}
              </p>
            )}
            <div className="grid grid-cols-[3rem_minmax(0,1fr)] gap-x-4 py-3.5">
              <span className="pt-0.5 text-xs tabular-nums text-muted-foreground">
                {timeOf(tax.created_at)}
              </span>
              <div className="min-w-0 space-y-1.5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold">
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-2 rounded-full",
                        change.source === "OWN" ? "bg-primary" : "bg-foreground",
                      )}
                    />
                    {role(tax)}
                  </span>
                  {showScope && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <CountryFlag iso3={tax.country} />
                      {countryLabel(tax.country, activeLanguage)} · {scopeLabel(tax, t)}
                    </span>
                  )}
                  {change.status !== "ACTIVE" && (
                    <ColocaStatusPill variant={STATUS_PILL[change.status]} size="sm">
                      {t(change.status === "SCHEDULED" ? "history_scheduled" : "history_superseded")}
                    </ColocaStatusPill>
                  )}
                </div>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 tabular-nums">
                  {change.previous ? (
                    <>
                      <span className="text-sm text-muted-foreground line-through decoration-muted-foreground/40">
                        {rateText(change.previous)}
                      </span>
                      <ArrowRight className="size-3.5 self-center text-muted-foreground" aria-hidden="true" />
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">{t("history_new")}:</span>
                  )}
                  <span className="text-base font-semibold">{rateText(tax)}</span>
                  <span className="text-xs text-muted-foreground">
                    {t(tax.bearer === "CLIENT" ? "bearer_client" : "bearer_coloca")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  <span className="text-foreground/80">{author(tax)}</span>
                  {tax.change_reason ? <> · “{tax.change_reason}”</> : <> · {t("history_reason_none")}</>}
                  {" · "}
                  {period(change)}
                </p>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
